import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { supabase } from "../../supabaseClient";
import EditPopup from "../common/EditPopup";

function SharedWithMe({ userId, isDarkMode, onPendingCountChange }) {
  const [shareData, setShareData] = useState({});

  // Send request popup
  const [showSendPopup, setShowSendPopup] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendError, setSendError] = useState("");
  const [sendLoading, setSendLoading] = useState(false);

  // Cancel/Disconnect popup (optional reason, shared for both flows)
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const [reasonTargetId, setReasonTargetId] = useState(null);
  const [reasonTargetEmail, setReasonTargetEmail] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [reasonLoading, setReasonLoading] = useState(false);
  const [reasonMode, setReasonMode] = useState("cancel"); // "cancel" or "disconnect"

  // Generic confirmation popup (delete / sender cancel)
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmBody, setConfirmBody] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Unread shared tasks per sender email
  const [unreadByEmail, setUnreadByEmail] = useState({});

  // Per-user shared content popup
  const [showShareDetailPopup, setShowShareDetailPopup] = useState(false);
  const [shareDetailUserId, setShareDetailUserId] = useState(null);
  const [shareDetailEmail, setShareDetailEmail] = useState("");
  const [shareDetailItems, setShareDetailItems] = useState([]);
  const [shareDetailListItems, setShareDetailListItems] = useState([]);
  const [shareDetailLoading, setShareDetailLoading] = useState(false);
  const [expandedShareId, setExpandedShareId] = useState(null);
  const [shareDetailTab, setShareDetailTab] = useState("tasks");
  const [shareDetailDirection, setShareDetailDirection] = useState("all");
  const [shareDetailCatFilter, setShareDetailCatFilter] = useState("");
  const [shareDetailSubCatFilter, setShareDetailSubCatFilter] = useState("");
  const [catNameMap, setCatNameMap] = useState({});
  const [listCatNameMap, setListCatNameMap] = useState({});
  const [shareDetailNoteItems, setShareDetailNoteItems] = useState([]);
  const [notesCatNameMap, setNotesCatNameMap] = useState({});
  const [shareDetailGroceryItems, setShareDetailGroceryItems] = useState([]);
  const [groceryCatNameMap, setGroceryCatNameMap] = useState({});

  // Edit popup state
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editSectionType, setEditSectionType] = useState("tasks");
  const [editCatData, setEditCatData] = useState({ categories: [], subcategories: [] });

  const TABLE_MAP = { tasks: "tasks", lists: "lists", notes: "notes", grocery: "grocery_table" };
  const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";

  const getStateSetter = (sectionType) => ({
    tasks: setShareDetailItems,
    lists: setShareDetailListItems,
    notes: setShareDetailNoteItems,
    grocery: setShareDetailGroceryItems,
  }[sectionType]);

  const handleToggleStatus = async (item, sectionType) => {
    const newStatus = item.status === "completed" ? "pending" : "completed";
    const setter = getStateSetter(sectionType);
    // Optimistic update
    setter(prev => prev.map(t => t.id === item.id ? { ...t, status: newStatus } : t));
    try {
      // Use sync-task API which updates original + ALL copies (uses service_role, no RLS issues)
      const syncRes = await fetch(`${API_URL}/api/sharing/sync-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ task_id: item.id, user_id: userId, updates: { status: newStatus }, section_type: sectionType }),
      });
      const syncData = await syncRes.json();
      if (!syncRes.ok) {
        console.error("Sync failed:", syncData);
        throw new Error(syncData.error || "Sync failed");
      }
      // Refresh popup data to reflect synced state from server
      refreshPopupData();
    } catch (e) {
      console.error("Error toggling status:", e);
      setter(prev => prev.map(t => t.id === item.id ? { ...t, status: item.status } : t));
    }
  };

  const getCatMapForSection = (sectionType) => ({
    tasks: catNameMap, lists: listCatNameMap, notes: notesCatNameMap, grocery: groceryCatNameMap,
  }[sectionType] || {});

  const buildEditCatData = (catMap, catId) => {
    const catKey = String(catId);
    const cat = catId ? (catMap[catId] || catMap[catKey]) : null;
    if (!catId || catKey === "0" || !cat) {
      return { categories: [{ id: "0", name: "General" }], subcategories: [] };
    }
    if (cat.parentId) {
      const parentName = cat.parentName || (catMap[cat.parentId] ? catMap[cat.parentId].name : "Unknown");
      return {
        categories: [{ id: cat.parentId, name: parentName }],
        subcategories: [{ id: catId, name: cat.name }],
      };
    }
    return { categories: [{ id: catId, name: cat.name }], subcategories: [] };
  };

  const handleEditItem = (item, sectionType) => {
    const catMap = getCatMapForSection(sectionType);
    const catId = item.category_id;
    let resolvedCatId = catId;
    // If no category or not found in catMap (check both number and string keys), treat as General
    if (!catId || (!catMap[catId] && !catMap[String(catId)])) {
      resolvedCatId = "0";
    }
    // Compute catData once here (stable reference), not in render
    const catData = buildEditCatData(catMap, resolvedCatId);
    setEditCatData(catData);
    setEditItem({
      ...item,
      category_id: resolvedCatId,
      _shared: item._dir === "received",
      _sent: item._dir === "sent",
    });
    setEditSectionType(sectionType);
    setShowEditPopup(true);
  };

  const handleSaveEdit = async (updatedItem) => {
    const setter = getStateSetter(editSectionType);
    const updateData = { title: updatedItem.title, description: updatedItem.description };
    if (editSectionType === "tasks" && updatedItem.due_date) {
      updateData.due_date = updatedItem.due_date;
    }
    try {
      // Use sync-task API which updates original + ALL copies (uses service_role, no RLS issues)
      const syncRes = await fetch(`${API_URL}/api/sharing/sync-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ task_id: updatedItem.id, user_id: userId, updates: updateData, section_type: editSectionType }),
      });
      const syncData = await syncRes.json();
      if (!syncRes.ok) {
        console.error("Sync failed:", syncData);
        throw new Error(syncData.error || "Sync failed");
      }
      setter(prev => prev.map(t => t.id === updatedItem.id ? { ...t, ...updateData } : t));
      setShowEditPopup(false);
      setEditItem(null);
      // Refresh popup data to reflect synced state from server
      refreshPopupData();
    } catch (e) {
      console.error("Error saving edit:", e);
    }
  };

  const fetchShareData = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("share_connection")
        .eq("id", userId)
        .single();

      if (error) throw error;
      const connections = data?.share_connection || {};
      setShareData(connections);

      const connectionPendingCount = Object.values(connections).filter(
        (entry) => !entry.is_sending && !entry.accept && !entry.cancel
      ).length;

      // Count unread: new items (share_visible=false) + updated items (share_update=true)
      // Count items that are either new (share_visible=false) or updated (share_update=true)
      const unreadFilter = (table) => supabase.from(table).select("shared_by").eq("user_id", userId).eq("is_share", true).not("shared_by", "is", null).or("share_visible.eq.false,share_update.eq.true");
      const [
        { data: unreadTasks }, { data: unreadLists }, { data: unreadNotes }, { data: unreadGrocery },
      ] = await Promise.all([
        unreadFilter("tasks"),
        unreadFilter("lists"),
        unreadFilter("notes"),
        unreadFilter("grocery_table"),
      ]);

      const emailCounts = {};
      [unreadTasks, unreadLists, unreadNotes, unreadGrocery].forEach(arr => {
        (arr || []).forEach((t) => {
          if (t.shared_by) emailCounts[t.shared_by] = (emailCounts[t.shared_by] || 0) + 1;
        });
      });
      setUnreadByEmail(emailCounts);

      const totalUnread = (unreadTasks?.length || 0) + (unreadLists?.length || 0) + (unreadNotes?.length || 0) + (unreadGrocery?.length || 0);
      if (onPendingCountChange) onPendingCountChange(connectionPendingCount + totalUnread, {
        pending: connectionPendingCount,
        tasks: unreadTasks?.length || 0,
        lists: unreadLists?.length || 0,
        notes: unreadNotes?.length || 0,
        grocery: unreadGrocery?.length || 0,
      });
    } catch (err) {
      console.error("Error fetching share_connection:", err);
    }
  }, [userId, onPendingCountChange]);

  useEffect(() => { fetchShareData(); }, [fetchShareData]);

  // Build unreadByUser from unreadByEmail keyed by user id (match via connections)
  const unreadByUser = {};
  Object.entries(shareData).forEach(([otherUserId, entry]) => {
    if (entry.email && unreadByEmail[entry.email]) {
      unreadByUser[otherUserId] = unreadByEmail[entry.email];
    }
  });

  // Open per-user shared content popup
  const openShareDetailPopup = async (otherUserId, email) => {
    setShareDetailUserId(otherUserId);
    setShareDetailEmail(email);
    setShowShareDetailPopup(true);
    setShareDetailLoading(true);
    setExpandedShareId(null);
    setShareDetailDirection("all");
    setShareDetailCatFilter("");
    setShareDetailSubCatFilter("");
    setCatNameMap({});

    try {
      const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";

      // Fetch all 4 sections in parallel
      const [tasksRes, listsRes, notesRes, groceryRes] = await Promise.all([
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${otherUserId}&section_type=tasks`),
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${otherUserId}&section_type=lists`),
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${otherUserId}&section_type=notes`),
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${otherUserId}&section_type=grocery`),
      ]);
      const tasksResult = await tasksRes.json();
      const listsResult = await listsRes.json();
      const notesResult = await notesRes.json();
      const groceryResult = await groceryRes.json();

      if (!tasksRes.ok) throw new Error(tasksResult.error || "Failed to fetch tasks");

      const allTasks = [...(tasksResult.received || []), ...(tasksResult.sent || [])];
      setShareDetailItems(allTasks);
      setCatNameMap(tasksResult.categories || {});

      const allLists = [...(listsResult.received || []), ...(listsResult.sent || [])];
      setShareDetailListItems(allLists);
      setListCatNameMap(listsResult.categories || {});

      const allNotes = [...(notesResult.received || []), ...(notesResult.sent || [])];
      setShareDetailNoteItems(allNotes);
      setNotesCatNameMap(notesResult.categories || {});

      const allGrocery = [...(groceryResult.received || []), ...(groceryResult.sent || [])];
      setShareDetailGroceryItems(allGrocery);
      setGroceryCatNameMap(groceryResult.categories || {});

      // Set default tab to first section with content
      const defaultTab = allTasks.length > 0 ? "tasks" : allLists.length > 0 ? "lists" : allNotes.length > 0 ? "notes" : allGrocery.length > 0 ? "grocery" : "tasks";
      setShareDetailTab(defaultTab);

      // Mark shared items from this user as seen (share_visible=true, share_update=false)
      if (unreadByEmail[email]) {
        const markNewSeen = (table) => supabase.from(table).update({ share_visible: true }).eq("user_id", userId).eq("is_share", true).eq("share_visible", false).eq("shared_by", email);
        const markUpdateSeen = (table) => supabase.from(table).update({ share_update: false }).eq("user_id", userId).eq("is_share", true).eq("share_update", true).eq("shared_by", email);
        await Promise.all([
          markNewSeen("tasks"), markNewSeen("lists"), markNewSeen("notes"), markNewSeen("grocery_table"),
          markUpdateSeen("tasks"), markUpdateSeen("lists"), markUpdateSeen("notes"), markUpdateSeen("grocery_table"),
        ]);
        fetchShareData();
      }
    } catch (err) {
      console.error("Error fetching shared tasks:", err);
    }
    setShareDetailLoading(false);
  };

  // Silent refresh popup data (no loading spinner)
  const refreshPopupData = useCallback(async () => {
    if (!showShareDetailPopup || !shareDetailUserId) return;
    try {
      const [tasksRes, listsRes, notesRes, groceryRes] = await Promise.all([
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${shareDetailUserId}&section_type=tasks`),
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${shareDetailUserId}&section_type=lists`),
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${shareDetailUserId}&section_type=notes`),
        fetch(`${API_URL}/api/sharing/shared-tasks?user_id=${userId}&other_user_id=${shareDetailUserId}&section_type=grocery`),
      ]);
      const [tasksResult, listsResult, notesResult, groceryResult] = await Promise.all([
        tasksRes.json(), listsRes.json(), notesRes.json(), groceryRes.json(),
      ]);
      setShareDetailItems([...(tasksResult.received || []), ...(tasksResult.sent || [])]);
      setCatNameMap(tasksResult.categories || {});
      setShareDetailListItems([...(listsResult.received || []), ...(listsResult.sent || [])]);
      setListCatNameMap(listsResult.categories || {});
      setShareDetailNoteItems([...(notesResult.received || []), ...(notesResult.sent || [])]);
      setNotesCatNameMap(notesResult.categories || {});
      setShareDetailGroceryItems([...(groceryResult.received || []), ...(groceryResult.sent || [])]);
      setGroceryCatNameMap(groceryResult.categories || {});
    } catch (e) { console.error("Error refreshing popup data:", e); }
  }, [showShareDetailPopup, shareDetailUserId, userId, API_URL]);

  // Stable refs for realtime callbacks (prevents channel teardown on every render)
  const fetchShareDataRef = useRef(fetchShareData);
  const refreshPopupDataRef = useRef(refreshPopupData);
  useEffect(() => { fetchShareDataRef.current = fetchShareData; }, [fetchShareData]);
  useEffect(() => { refreshPopupDataRef.current = refreshPopupData; }, [refreshPopupData]);

  // Realtime subscription (stable -- only depends on userId)
  useEffect(() => {
    if (!userId) return;
    const handleSharedChange = () => {
      fetchShareDataRef.current();
      refreshPopupDataRef.current();
    };
    const channel = supabase
      .channel(`sharing_${userId}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${userId}` }, () => fetchShareDataRef.current())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "tasks", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "tasks", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "lists", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "lists", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notes", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notes", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "grocery_table", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "grocery_table", filter: `user_id=eq.${userId}` }, handleSharedChange)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  // ---- Confirmation popup helper ----
  const openConfirmPopup = (title, body, action) => {
    setConfirmTitle(title);
    setConfirmBody(body);
    setConfirmAction(() => action);
    setShowConfirmPopup(true);
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      await confirmAction();
    } finally {
      setConfirmLoading(false);
      setShowConfirmPopup(false);
      setConfirmAction(null);
    }
  };

  // ---- SEND REQUEST ----
  const handleOpenSendPopup = () => {
    setSendEmail("");
    setSendError("");
    setShowSendPopup(true);
  };

  const handleSendRequest = async () => {
    const trimmedEmail = sendEmail.trim().toLowerCase();
    if (!trimmedEmail) { setSendError("Please enter an email address."); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) { setSendError("Please enter a valid email address."); return; }

    setSendLoading(true);
    setSendError("");

    try {
      const { data: receiverProfile, error: profileError } = await supabase
        .from("profiles").select("id, email").eq("email", trimmedEmail).maybeSingle();
      if (profileError) throw profileError;
      if (!receiverProfile) { setSendError("No user found with this email address."); setSendLoading(false); return; }

      const receiverId = receiverProfile.id;
      if (receiverId === userId) { setSendError("You cannot send a request to yourself."); setSendLoading(false); return; }

      const { data: senderProfile } = await supabase
        .from("profiles").select("share_connection, email").eq("id", userId).single();
      const senderConnections = senderProfile?.share_connection || {};
      const senderEmail = senderProfile?.email || "";

      if (senderConnections[receiverId] && !senderConnections[receiverId].cancel) { setSendError("Connection request already exists with this user."); setSendLoading(false); return; }

      const updatedSender = { ...senderConnections, [receiverId]: { email: trimmedEmail, is_sending: true, accept: false, cancel: false, message: "" } };
      const { error: sErr } = await supabase.from("profiles").update({ share_connection: updatedSender }).eq("id", userId);
      if (sErr) throw sErr;

      const { data: receiverData } = await supabase.from("profiles").select("share_connection").eq("id", receiverId).single();
      const receiverConnections = receiverData?.share_connection || {};
      const updatedReceiver = { ...receiverConnections, [userId]: { email: senderEmail, is_sending: false, accept: false, cancel: false, message: "" } };
      const { error: rErr } = await supabase.from("profiles").update({ share_connection: updatedReceiver }).eq("id", receiverId);
      if (rErr) throw rErr;

      setShowSendPopup(false);
      setSendEmail("");
      fetchShareData();
    } catch (err) {
      console.error("Error sending connection request:", err);
      setSendError("Failed to send request. Please try again.");
    } finally {
      setSendLoading(false);
    }
  };

  // ---- Remove entry from both sides ----
  const removeBothSides = async (targetUserId) => {
    const updatedSender = { ...shareData };
    delete updatedSender[targetUserId];
    await supabase.from("profiles").update({ share_connection: updatedSender }).eq("id", userId);

    const { data: receiverData } = await supabase.from("profiles").select("share_connection").eq("id", targetUserId).single();
    if (receiverData?.share_connection) {
      const updatedReceiver = { ...receiverData.share_connection };
      delete updatedReceiver[userId];
      await supabase.from("profiles").update({ share_connection: updatedReceiver }).eq("id", targetUserId);
    }
    fetchShareData();
  };

  // ---- SENDER CANCEL (with confirmation) ----
  const handleSenderCancel = (targetUserId, email) => {
    openConfirmPopup("Cancel Request", `Cancel connection request to ${email}?`, () => removeBothSides(targetUserId));
  };

  // ---- RECEIVER ACCEPT ----
  const handleReceiverAccept = async (senderUserId) => {
    try {
      const updatedReceiver = { ...shareData };
      if (updatedReceiver[senderUserId]) updatedReceiver[senderUserId] = { ...updatedReceiver[senderUserId], accept: true };
      await supabase.from("profiles").update({ share_connection: updatedReceiver }).eq("id", userId);

      const { data: senderData } = await supabase.from("profiles").select("share_connection").eq("id", senderUserId).single();
      if (senderData?.share_connection?.[userId]) {
        const updatedSender = { ...senderData.share_connection };
        updatedSender[userId] = { ...updatedSender[userId], accept: true };
        await supabase.from("profiles").update({ share_connection: updatedSender }).eq("id", senderUserId);
      }
      fetchShareData();
    } catch (err) {
      console.error("Error accepting request:", err);
    }
  };

  // ---- RECEIVER CANCEL / DISCONNECT (shared popup with optional reason) ----
  const handleOpenReasonPopup = (targetUserId, email, mode) => {
    setReasonTargetId(targetUserId);
    setReasonTargetEmail(email);
    setReasonText("");
    setReasonMode(mode);
    setShowReasonPopup(true);
  };

  const handleReasonConfirm = async () => {
    if (!reasonTargetId) return;
    setReasonLoading(true);
    try {
      // Get current user's email for default message on other side
      const { data: myProfile } = await supabase.from("profiles").select("email").eq("id", userId).single();
      const myEmail = myProfile?.email || "";

      const userReason = reasonText.trim();
      // Message for the other side: user's reason if provided, otherwise default
      const defaultMsg = reasonMode === "disconnect"
        ? `User ${myEmail} disconnected you`
        : `User ${myEmail} cancelled your request`;
      const messageForOther = userReason || defaultMsg;
      // Message for own side: user's reason or empty
      const messageForSelf = userReason;

      // Update own side: cancel = true
      const updatedSelf = { ...shareData };
      if (updatedSelf[reasonTargetId]) {
        updatedSelf[reasonTargetId] = { ...updatedSelf[reasonTargetId], cancel: true, accept: false, message: messageForSelf };
      }
      await supabase.from("profiles").update({ share_connection: updatedSelf }).eq("id", userId);

      // Update other side: cancel = true with message
      const { data: otherData } = await supabase.from("profiles").select("share_connection").eq("id", reasonTargetId).single();
      if (otherData?.share_connection?.[userId]) {
        const updatedOther = { ...otherData.share_connection };
        updatedOther[userId] = { ...updatedOther[userId], cancel: true, accept: false, message: messageForOther };
        await supabase.from("profiles").update({ share_connection: updatedOther }).eq("id", reasonTargetId);
      }

      // On disconnect, clean up all shared items between both users
      if (reasonMode === "disconnect") {
        const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";
        try {
          await fetch(`${API_URL}/api/sharing/disconnect-cleanup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_a_id: userId, user_b_id: reasonTargetId }),
          });
        } catch (cleanupErr) {
          console.error("Error cleaning up shared data:", cleanupErr);
        }
      }

      setShowReasonPopup(false);
      fetchShareData();
    } catch (err) {
      console.error("Error in cancel/disconnect:", err);
    } finally {
      setReasonLoading(false);
    }
  };

  // ---- DELETE OWN ENTRY (after cancel, with confirmation) ----
  const handleDeleteEntry = (targetUserId, email) => {
    openConfirmPopup("Delete Entry", `Remove cancelled entry for ${email}?`, async () => {
      const updated = { ...shareData };
      delete updated[targetUserId];
      await supabase.from("profiles").update({ share_connection: updated }).eq("id", userId);
      fetchShareData();
    });
  };

  // Categorize entries
  const sentPending = [];
  const receivedPending = [];
  const acceptedConnections = [];
  const cancelledEntries = [];

  Object.entries(shareData).forEach(([otherUserId, entry]) => {
    if (entry.cancel) cancelledEntries.push({ id: otherUserId, ...entry });
    else if (entry.accept) acceptedConnections.push({ id: otherUserId, ...entry });
    else if (entry.is_sending) sentPending.push({ id: otherUserId, ...entry });
    else receivedPending.push({ id: otherUserId, ...entry });
  });

  // Categorize for "has any content" check
  const hasContent = sentPending.length > 0 || receivedPending.length > 0 || acceptedConnections.length > 0 || cancelledEntries.length > 0;

  return (
    <div className="shared-with-me-container" style={{ padding: "0 1.5em 8px" }}>
      {/* Send Request Button */}
      <div style={{ marginBottom: "10px" }}>
        <button
          className="btn w-100"
          onClick={handleOpenSendPopup}
          style={{
            background: "linear-gradient(135deg, var(--teal-bg), rgba(var(--teal-bg-rag), 0.85))",
            color: "#fff",
            border: "none",
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 500,
            fontSize: "0.75rem",
            padding: "6px 12px",
            borderRadius: "8px",
          }}
        >
          <i className="bi bi-person-plus me-2"></i>Send Request
        </button>
      </div>

      {/* Scrollable list area matching sessionBox */}
      <div style={{ maxHeight: "280px", overflowY: "auto", scrollbarWidth: "none", msOverflowStyle: "none" }}>

        {/* Received Pending Requests */}
        {receivedPending.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", paddingLeft: "2px" }}>
              Incoming Requests ({receivedPending.length})
            </div>
            {receivedPending.map((entry) => (
              <div
                key={entry.id}
                className="d-flex justify-content-between align-items-center"
                style={{
                  background: "var(--background)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  padding: "7px 12px",
                  marginBottom: "6px",
                }}
              >
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                  <i className="bi bi-person-circle" style={{ fontSize: "16px", color: "var(--teal-bg)" }}></i>
                  <span className="text-truncate" style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: "0.8rem", fontWeight: 500, color: "var(--text)" }}>{entry.email}</span>
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                  <button
                    className="btn py-0 px-2"
                    onClick={() => handleReceiverAccept(entry.id)}
                    title="Accept"
                    style={{ background: "var(--teal-bg)", color: "#fff", fontSize: "0.75rem", borderRadius: "8px", lineHeight: "22px" }}
                  >
                    <i className="bi bi-check-lg"></i>
                  </button>
                  <button
                    className="btn py-0 px-2"
                    onClick={() => handleOpenReasonPopup(entry.id, entry.email, "cancel")}
                    title="Cancel"
                    style={{ background: "#ef4444", color: "#fff", fontSize: "0.75rem", borderRadius: "8px", lineHeight: "22px" }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sent Pending Requests */}
        {sentPending.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", paddingLeft: "2px" }}>
              Sent Requests
            </div>
            {sentPending.map((entry) => (
              <div
                key={entry.id}
                className="d-flex justify-content-between align-items-center"
                style={{
                  background: "var(--background)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  padding: "7px 12px",
                  marginBottom: "6px",
                }}
              >
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                  <i className="bi bi-send-fill" style={{ fontSize: "14px", color: "#f59e0b" }}></i>
                  <div style={{ minWidth: 0 }}>
                    <span className="text-truncate d-block" style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: "0.8rem", fontWeight: 500, color: "var(--text)" }}>{entry.email}</span>
                    <span style={{ fontSize: "0.65rem", fontWeight: 500, color: "#f59e0b" }}>Pending</span>
                  </div>
                </div>
                <button
                  className="btn py-0 px-2 flex-shrink-0"
                  onClick={() => handleSenderCancel(entry.id, entry.email)}
                  title="Cancel Request"
                  style={{ background: "#ef4444", color: "#fff", fontSize: "0.7rem", borderRadius: "8px", lineHeight: "22px" }}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Accepted Connections */}
        {acceptedConnections.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", paddingLeft: "2px" }}>
              Connections
            </div>
            {acceptedConnections.map((entry) => {
              const unreadCount = unreadByUser[entry.id] || 0;
              return (
                <div
                  key={entry.id}
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: "var(--background)",
                    border: unreadCount > 0 ? "1px solid #ef4444" : "1px solid var(--border-color)",
                    borderRadius: "8px",
                    padding: "7px 12px",
                    marginBottom: "6px",
                    cursor: "pointer",
                  }}
                  onClick={() => openShareDetailPopup(entry.id, entry.email)}
                >
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <i className="bi bi-person-check-fill" style={{ color: "var(--teal-bg)", fontSize: "16px" }}></i>
                    <span className="text-truncate" style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: "0.8rem", fontWeight: 500, color: "var(--text)" }}>{entry.email}</span>
                    {unreadCount > 0 && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        background: "#ef4444", color: "#fff",
                        fontSize: "0.6rem", fontWeight: 700,
                        minWidth: "20px", height: "20px", padding: "0 4px",
                        borderRadius: "4px", lineHeight: 1, flexShrink: 0,
                      }}>
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                  <button
                    className="btn py-0 px-2 flex-shrink-0"
                    onClick={(e) => { e.stopPropagation(); handleOpenReasonPopup(entry.id, entry.email, "disconnect"); }}
                    title="Disconnect"
                    style={{ background: "transparent", color: "#ef4444", fontSize: "0.65rem", borderRadius: "6px", lineHeight: "18px", border: "1px solid var(--border-color)", fontWeight: 500 }}
                  >
                    Disconnect
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Cancelled Entries */}
        {cancelledEntries.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", paddingLeft: "2px" }}>
              Cancelled
            </div>
            {cancelledEntries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  background: "var(--background)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  padding: "7px 12px",
                  marginBottom: "6px",
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <i className="bi bi-x-circle-fill" style={{ color: "#ef4444", fontSize: "14px" }}></i>
                    <span className="text-truncate" style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: "0.8rem", fontWeight: 500, color: "var(--text)" }}>{entry.email}</span>
                  </div>
                  <button
                    className="btn py-0 px-1 flex-shrink-0"
                    onClick={() => handleDeleteEntry(entry.id, entry.email)}
                    title="Delete"
                    style={{ background: "transparent", color: "var(--text)", opacity: 0.5, fontSize: "14px", lineHeight: "20px" }}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
                {entry.message && (
                  <div style={{ marginTop: "6px", marginLeft: "24px", fontSize: "0.7rem", fontStyle: "italic", color: isDarkMode ? "#fbbf24" : "#92400e", background: isDarkMode ? "rgba(251,191,36,0.08)" : "rgba(146,64,14,0.06)", padding: "4px 8px", borderRadius: "6px" }}>
                    {entry.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Empty state */}
      {!hasContent && (
        <div className="text-center" style={{ padding: "16px 8px", color: "var(--text)", opacity: 0.5 }}>
          <i className="bi bi-people" style={{ fontSize: "24px", display: "block", marginBottom: "8px" }}></i>
          <span style={{ fontSize: "0.8rem", fontFamily: "'Inter', system-ui, sans-serif" }}>No connections yet</span>
        </div>
      )}

      {/* ---- SEND REQUEST POPUP ---- */}
      <Modal show={showSendPopup} onHide={() => !sendLoading && setShowSendPopup(false)} centered className="task-popup" backdrop={sendLoading ? "static" : true}>
        <Modal.Header closeButton={!sendLoading}>
          <Modal.Title style={{ fontSize: "1rem" }}>Send Connection Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label style={{ fontSize: "0.8rem", color: "var(--text)", marginBottom: "6px", display: "block" }}>Enter user email address</label>
          <input
            type="email"
            className="form-control"
            placeholder="user@example.com"
            value={sendEmail}
            onChange={(e) => { setSendEmail(e.target.value); setSendError(""); }}
            autoComplete="off"
            disabled={sendLoading}
            onKeyDown={(e) => { if (e.key === "Enter") handleSendRequest(); }}
          />
          {sendError && <small className="text-danger d-block mt-1">{sendError}</small>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowSendPopup(false)} disabled={sendLoading}>Cancel</Button>
          <Button size="sm" onClick={handleSendRequest} disabled={sendLoading || !sendEmail.trim()} style={{ background: "#10b981", border: "none" }}>
            {sendLoading ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <i className="bi bi-send me-1"></i>}
            Send
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- CANCEL / DISCONNECT POPUP (with optional reason) ---- */}
      <Modal show={showReasonPopup} onHide={() => !reasonLoading && setShowReasonPopup(false)} centered className="task-popup" backdrop={reasonLoading ? "static" : true}>
        <Modal.Header closeButton={!reasonLoading}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {reasonMode === "disconnect" ? "Disconnect" : "Cancel Connection Request"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "0.85rem", color: "var(--text)", marginBottom: "12px" }}>
            {reasonMode === "disconnect"
              ? `Are you sure you want to disconnect from ${reasonTargetEmail}?`
              : `Are you sure you want to cancel this request from ${reasonTargetEmail}?`}
          </p>
          <label style={{ fontSize: "0.8rem", color: "var(--text)", marginBottom: "6px", display: "block" }}>Reason (optional)</label>
          <textarea
            className="form-control"
            rows={2}
            placeholder="Enter a reason..."
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            disabled={reasonLoading}
          />
          <small style={{ fontSize: "0.7rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: "4px", display: "block" }}>
            If left empty, a default message will be shown to the other user.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowReasonPopup(false)} disabled={reasonLoading}>Back</Button>
          <Button variant="danger" size="sm" onClick={handleReasonConfirm} disabled={reasonLoading}>
            {reasonLoading && <Spinner as="span" animation="border" size="sm" className="me-1" />}
            {reasonMode === "disconnect" ? "Disconnect" : "Cancel Request"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- GENERIC CONFIRMATION POPUP (delete / disconnect / sender cancel) ---- */}
      <Modal show={showConfirmPopup} onHide={() => !confirmLoading && setShowConfirmPopup(false)} centered className="task-popup confirmation-popup" backdrop={confirmLoading ? "static" : true}>
        <Modal.Header closeButton={!confirmLoading}>
          <Modal.Title>{confirmTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="confirmation-content">
            <div className="confirmation-icon">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <p className="confirmation-text">{confirmBody}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-popup-cancel" onClick={() => setShowConfirmPopup(false)} disabled={confirmLoading}>Cancel</Button>
          <Button className="btn-popup-delete" onClick={executeConfirmAction} disabled={confirmLoading}>
            {confirmLoading ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : null}
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- SHARED CONTENT DETAIL POPUP (per connection) ---- */}
      <Modal
        show={showShareDetailPopup}
        onHide={() => setShowShareDetailPopup(false)}
        centered
        size="xl"
        className="task-popup export-popup"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            <i className="bi bi-share-fill me-2" style={{ color: "var(--teal-bg)" }}></i>
            Shared with {shareDetailEmail}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "75vh", overflow: "hidden", padding: 0, display: "flex", flexDirection: "column" }}>
          {shareDetailLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ flexGrow: 1 }}>
              <Spinner animation="border" style={{ color: "var(--teal-bg)" }} />
              <p style={{ fontSize: "0.85rem", color: "var(--text)", opacity: 0.6, marginTop: "12px", marginBottom: 0 }}>Loading shared content...</p>
            </div>
          ) : (shareDetailItems.length === 0 && shareDetailListItems.length === 0 && shareDetailNoteItems.length === 0 && shareDetailGroceryItems.length === 0) ? (
            <div className="text-center py-5" style={{ color: "var(--text)", opacity: 0.5, flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-inbox" style={{ fontSize: "32px", display: "block", marginBottom: "10px" }}></i>
              <p style={{ fontSize: "0.85rem", margin: 0 }}>No shared content yet with this user.</p>
            </div>
          ) : (() => {
            const allItems = shareDetailItems;

            // Direction filter
            const dirFiltered = shareDetailDirection === "all"
              ? allItems
              : allItems.filter(i => i._dir === shareDetailDirection);

            // Direction counts
            const receivedCount = allItems.filter(i => i._dir === "received").length;
            const sentCount = allItems.filter(i => i._dir === "sent").length;

            // Build parent + sub categories from items
            const parentCatsMap = {};
            const subCatsMap = {};
            let hasGeneral = false;
            dirFiltered.forEach(item => {
              if (!item.category_id) {
                hasGeneral = true;
                return;
              }
              const cat = catNameMap[item.category_id];
              if (!cat) {
                hasGeneral = true;
                return;
              }
              if (cat.parentId) {
                subCatsMap[item.category_id] = cat;
                if (catNameMap[cat.parentId]) parentCatsMap[cat.parentId] = catNameMap[cat.parentId];
              } else {
                parentCatsMap[item.category_id] = cat;
              }
            });
            const parentCatOptions = [
              ...(hasGeneral ? [["general", { name: "General" }]] : []),
              ...Object.entries(parentCatsMap).sort((a, b) => a[1].name.localeCompare(b[1].name)),
            ];
            const subCatOptions = shareDetailCatFilter && shareDetailCatFilter !== "general"
              ? Object.entries(subCatsMap).filter(([, c]) => String(c.parentId) === String(shareDetailCatFilter)).sort((a, b) => a[1].name.localeCompare(b[1].name))
              : [];

            // Category filter
            let filtered = dirFiltered;
            if (shareDetailSubCatFilter) {
              filtered = filtered.filter(i => String(i.category_id) === String(shareDetailSubCatFilter));
            } else if (shareDetailCatFilter === "general") {
              filtered = filtered.filter(i => !i.category_id || !catNameMap[i.category_id]);
            } else if (shareDetailCatFilter) {
              const subIds = Object.entries(subCatsMap)
                .filter(([, c]) => String(c.parentId) === String(shareDetailCatFilter))
                .map(([id]) => String(id));
              const validIds = [String(shareDetailCatFilter), ...subIds];
              filtered = filtered.filter(i => validIds.includes(String(i.category_id)));
            }

            const getCatLabel = (catId) => {
              if (!catId) return "General";
              const cat = catNameMap[catId];
              if (!cat) return "General";
              if (cat.parentName) return `${cat.parentName} \u203A ${cat.name}`;
              if (cat.parentId && catNameMap[cat.parentId]) return `${catNameMap[cat.parentId].name} \u203A ${cat.name}`;
              return cat.name;
            };

            const selectStyle = {
              fontSize: "0.78rem", borderRadius: "8px", padding: "5px 10px",
              border: "1px solid var(--border-color)", background: "var(--background)",
              color: "var(--text)", cursor: "pointer", outline: "none", minWidth: 0, flex: 1,
            };

            return (
              <>
                {/* Section tabs */}
                <div style={{ display: "flex", borderBottom: "1px solid var(--border-color)", padding: "10px 16px", flexShrink: 0, gap: "16px" }}>
                  <div
                    onClick={() => { setShareDetailTab("tasks"); setShareDetailDirection("all"); setShareDetailCatFilter(""); setShareDetailSubCatFilter(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                      color: shareDetailTab === "tasks" ? "var(--teal-bg)" : "var(--text)",
                      opacity: shareDetailTab === "tasks" ? 1 : 0.5,
                      borderBottom: shareDetailTab === "tasks" ? "2px solid var(--teal-bg)" : "2px solid transparent",
                      paddingBottom: "4px",
                    }}
                  >
                    <i className="bi bi-check2-square" style={{ fontSize: "0.85rem" }}></i>
                    Tasks
                    <span style={{ fontSize: "0.6rem", fontWeight: 600, background: "rgba(var(--teal-bg-rag), 0.12)", padding: "1px 6px", borderRadius: "8px" }}>
                      {allItems.length}
                    </span>
                  </div>
                  <div
                    onClick={() => { setShareDetailTab("lists"); setShareDetailDirection("all"); setShareDetailCatFilter(""); setShareDetailSubCatFilter(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                      color: shareDetailTab === "lists" ? "var(--teal-bg)" : "var(--text)",
                      opacity: shareDetailTab === "lists" ? 1 : 0.5,
                      borderBottom: shareDetailTab === "lists" ? "2px solid var(--teal-bg)" : "2px solid transparent",
                      paddingBottom: "4px",
                    }}
                  >
                    <i className="bi bi-list-task" style={{ fontSize: "0.85rem" }}></i>
                    Lists
                    <span style={{ fontSize: "0.6rem", fontWeight: 600, background: "rgba(var(--teal-bg-rag), 0.12)", padding: "1px 6px", borderRadius: "8px" }}>
                      {shareDetailListItems.length}
                    </span>
                  </div>
                  <div
                    onClick={() => { setShareDetailTab("notes"); setShareDetailDirection("all"); setShareDetailCatFilter(""); setShareDetailSubCatFilter(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                      color: shareDetailTab === "notes" ? "var(--teal-bg)" : "var(--text)",
                      opacity: shareDetailTab === "notes" ? 1 : 0.5,
                      borderBottom: shareDetailTab === "notes" ? "2px solid var(--teal-bg)" : "2px solid transparent",
                      paddingBottom: "4px",
                    }}
                  >
                    <i className="bi bi-journal-text" style={{ fontSize: "0.85rem" }}></i>
                    Notes
                    <span style={{ fontSize: "0.6rem", fontWeight: 600, background: "rgba(var(--teal-bg-rag), 0.12)", padding: "1px 6px", borderRadius: "8px" }}>
                      {shareDetailNoteItems.length}
                    </span>
                  </div>
                  <div
                    onClick={() => { setShareDetailTab("grocery"); setShareDetailDirection("all"); setShareDetailCatFilter(""); setShareDetailSubCatFilter(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                      color: shareDetailTab === "grocery" ? "var(--teal-bg)" : "var(--text)",
                      opacity: shareDetailTab === "grocery" ? 1 : 0.5,
                      borderBottom: shareDetailTab === "grocery" ? "2px solid var(--teal-bg)" : "2px solid transparent",
                      paddingBottom: "4px",
                    }}
                  >
                    <i className="bi bi-cart3" style={{ fontSize: "0.85rem" }}></i>
                    Grocery
                    <span style={{ fontSize: "0.6rem", fontWeight: 600, background: "rgba(var(--teal-bg-rag), 0.12)", padding: "1px 6px", borderRadius: "8px" }}>
                      {shareDetailGroceryItems.length}
                    </span>
                  </div>
                </div>

                {shareDetailTab === "tasks" && (<>
                {/* Filters bar */}
                <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-color)", flexShrink: 0 }}>
                  {/* Direction toggle */}
                  <div className="d-flex gap-1 mb-2">
                    {[
                      { key: "all", label: "All", count: allItems.length, color: "var(--teal-bg)" },
                      { key: "received", label: "Received", count: receivedCount, color: "var(--teal-bg)" },
                      { key: "sent", label: "Sent", count: sentCount, color: "#f59e0b" },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => { setShareDetailDirection(opt.key); setShareDetailCatFilter(""); setShareDetailSubCatFilter(""); }}
                        style={{
                          fontSize: "0.72rem", fontWeight: shareDetailDirection === opt.key ? 600 : 400,
                          background: shareDetailDirection === opt.key ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.15)" : "rgba(var(--teal-bg-rag), 0.1)") : "transparent",
                          color: shareDetailDirection === opt.key ? opt.color : "var(--text)",
                          border: `1px solid ${shareDetailDirection === opt.key ? opt.color : "var(--border-color)"}`,
                          borderRadius: "8px", padding: "4px 12px", cursor: "pointer",
                          opacity: shareDetailDirection === opt.key ? 1 : 0.6,
                        }}
                      >
                        {opt.label} ({opt.count})
                      </button>
                    ))}
                  </div>
                  {/* Category + Subcategory dropdowns */}
                  <div className="d-flex gap-2">
                    <select
                      value={shareDetailCatFilter}
                      onChange={(e) => { setShareDetailCatFilter(e.target.value); setShareDetailSubCatFilter(""); }}
                      style={selectStyle}
                    >
                      <option value="">All Categories</option>
                      {parentCatOptions.map(([id, cat]) => (
                        <option key={id} value={id}>{cat.name}</option>
                      ))}
                    </select>
                    <select
                      value={shareDetailSubCatFilter}
                      onChange={(e) => setShareDetailSubCatFilter(e.target.value)}
                      disabled={!shareDetailCatFilter || shareDetailCatFilter === "general" || subCatOptions.length === 0}
                      style={{ ...selectStyle, opacity: (!shareDetailCatFilter || shareDetailCatFilter === "general" || subCatOptions.length === 0) ? 0.5 : 1 }}
                    >
                      <option value="">All Subcategories</option>
                      {subCatOptions.map(([id, cat]) => (
                        <option key={id} value={id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Items list */}
                <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
                  {filtered.length === 0 ? (
                    <div className="text-center py-4" style={{ color: "var(--text)", opacity: 0.4, fontSize: "0.85rem" }}>
                      No tasks found.
                    </div>
                  ) : (
                    filtered.map((item, idx) => {
                      const isPending = item.status !== "completed";
                      const catLabel = getCatLabel(item.category_id);

                      const isNew = item._dir === "received" && item.share_visible === false;
                      const isUpdated = !isNew && item.share_update === true;

                      return (
                        <div
                          key={`${item.id}-${idx}`}
                          className="d-flex align-items-start mb-2"
                          onClick={() => handleEditItem(item, "tasks")}
                          style={{
                            background: (isNew || isUpdated) ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.06)" : "rgba(var(--teal-bg-rag), 0.03)") : "var(--background)",
                            border: isNew ? "1px solid var(--teal-bg)" : isUpdated ? "1px solid #f59e0b" : "1px solid var(--border-color)",
                            borderRadius: "10px",
                            padding: "10px 12px",
                            transition: "background 0.15s",
                            cursor: "pointer",
                          }}
                        >
                          <div
                            className={`task-checkbox ${!isPending ? 'checked' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(item, "tasks"); }}
                            style={{ marginRight: "12px", flexShrink: 0 }}
                          >
                            {!isPending && <i className="bi bi-check"></i>}
                          </div>
                          <div className="task-details" style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2">
                              <p style={{
                                margin: 0, fontSize: "0.85rem", fontWeight: 500, color: "var(--text)",
                                textDecoration: !isPending ? "line-through" : "none",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}>{item.title}</p>
                              {isNew && (
                                <span style={{
                                  fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                  background: "var(--teal-bg)", color: "#fff",
                                  padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                }}>New</span>
                              )}
                              {isUpdated && (
                                <span style={{
                                  fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                  background: "#f59e0b", color: "#fff",
                                  padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                }}>Updated</span>
                              )}
                            </div>
                            {item.description && (
                              <div style={{
                                fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: "3px",
                                overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                                WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                              }}>
                                <span dangerouslySetInnerHTML={{ __html: item.description.substring(0, 200) }} />
                              </div>
                            )}
                            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                              {isPending
                                ? <span className="message-success-label fw-normal" style={{ backgroundColor: "var(--orange-bg)", padding: "2px 10px" }}>Pending</span>
                                : <span className="message-success-label fw-normal" style={{ padding: "2px 10px" }}>Completed</span>
                              }
                              {item.due_date && (
                                <span className="due_task_date" style={{ fontSize: "0.7rem" }}>
                                  <i className="bi bi-calendar-event me-1"></i>{new Date(item.due_date).toLocaleDateString()}
                                </span>
                              )}
                              {catLabel && (
                                <span style={{
                                  fontSize: "0.6rem", fontWeight: 500,
                                  background: isDarkMode ? "rgba(var(--teal-bg-rag), 0.12)" : "rgba(var(--teal-bg-rag), 0.08)",
                                  color: "var(--teal-bg)", padding: "2px 8px", borderRadius: "6px",
                                }}>
                                  <i className="bi bi-folder2 me-1"></i>{catLabel}
                                </span>
                              )}
                              <span style={{
                                fontSize: "0.6rem", fontWeight: 500,
                                background: item._dir === "received"
                                  ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.1)" : "rgba(var(--teal-bg-rag), 0.06)")
                                  : (isDarkMode ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.06)"),
                                color: item._dir === "received" ? "var(--teal-bg)" : "#f59e0b",
                                padding: "2px 8px", borderRadius: "6px",
                              }}>
                                {item._dir === "received" ? <><i className="bi bi-arrow-down-short"></i>Received</> : <><i className="bi bi-arrow-up-short"></i>Sent</>}
                              </span>
                            </div>
                          </div>
                          <button
                            className="btn py-0 px-1 flex-shrink-0 ms-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmPopup(
                                "Delete Shared Item",
                                "Remove this item?",
                                async () => {
                                  await supabase.from("tasks").delete().eq("id", item.id);
                                  setShareDetailItems(prev => prev.filter(t => t.id !== item.id));
                                  fetchShareData();
                                }
                              );
                            }}
                            title="Delete"
                            style={{ background: "transparent", color: "var(--text)", opacity: 0.3, fontSize: "14px", marginTop: "2px" }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
                </>)}

                {shareDetailTab === "lists" && (() => {
                  const listItems = shareDetailListItems;
                  let filteredLists = [...listItems];
                  if (shareDetailDirection !== "all") filteredLists = filteredLists.filter(i => i._dir === shareDetailDirection);
                  const listReceivedCount = listItems.filter(i => i._dir === "received").length;
                  const listSentCount = listItems.filter(i => i._dir === "sent").length;

                  if (shareDetailCatFilter) {
                    if (shareDetailCatFilter === "general") {
                      filteredLists = filteredLists.filter(i => !i.category_id || i.category_id === 0);
                    } else {
                      const subIds = Object.entries(listCatNameMap).filter(([, c]) => String(c.parentId) === String(shareDetailCatFilter)).map(([id]) => id);
                      const validIds = [String(shareDetailCatFilter), ...subIds];
                      filteredLists = filteredLists.filter(i => validIds.includes(String(i.category_id)));
                    }
                  }

                  const getListCatLabel = (catId) => {
                    if (!catId) return "General";
                    const cat = listCatNameMap[catId];
                    if (!cat) return "General";
                    if (cat.parentName) return `${cat.parentName} \u203A ${cat.name}`;
                    if (cat.parentId && listCatNameMap[cat.parentId]) return `${listCatNameMap[cat.parentId].name} \u203A ${cat.name}`;
                    return cat.name;
                  };

                  const listParentCatOptions = Object.entries(listCatNameMap).filter(([, c]) => !c.parentId);

                  return (
                    <>
                      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-color)", flexShrink: 0 }}>
                        <div className="d-flex gap-1 mb-2">
                          {[
                            { key: "all", label: "All", count: listItems.length, color: "var(--teal-bg)" },
                            { key: "received", label: "Received", count: listReceivedCount, color: "var(--teal-bg)" },
                            { key: "sent", label: "Sent", count: listSentCount, color: "#f59e0b" },
                          ].map(opt => (
                            <button
                              key={opt.key}
                              onClick={() => { setShareDetailDirection(opt.key); setShareDetailCatFilter(""); }}
                              style={{
                                fontSize: "0.72rem", fontWeight: shareDetailDirection === opt.key ? 600 : 400,
                                background: shareDetailDirection === opt.key ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.15)" : "rgba(var(--teal-bg-rag), 0.1)") : "transparent",
                                color: shareDetailDirection === opt.key ? opt.color : "var(--text)",
                                border: `1px solid ${shareDetailDirection === opt.key ? opt.color : "var(--border-color)"}`,
                                borderRadius: "8px", padding: "4px 12px", cursor: "pointer",
                                opacity: shareDetailDirection === opt.key ? 1 : 0.6,
                              }}
                            >
                              {opt.label} ({opt.count})
                            </button>
                          ))}
                        </div>
                        <div className="d-flex gap-2">
                          <select
                            value={shareDetailCatFilter}
                            onChange={(e) => setShareDetailCatFilter(e.target.value)}
                            style={{ fontSize: "0.78rem", borderRadius: "8px", padding: "5px 10px", border: "1px solid var(--border-color)", background: "var(--background)", color: "var(--text)", cursor: "pointer", outline: "none", flex: 1 }}
                          >
                            <option value="">All Categories</option>
                            {listParentCatOptions.map(([id, cat]) => (
                              <option key={id} value={id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
                        {filteredLists.length === 0 ? (
                          <div className="text-center py-4" style={{ color: "var(--text)", opacity: 0.4, fontSize: "0.85rem" }}>
                            No lists found.
                          </div>
                        ) : (
                          filteredLists.map((item, idx) => {
                            const isPending = item.status !== "completed";
                            const catLabel = getListCatLabel(item.category_id);
                            const isNew = item._dir === "received" && item.share_visible === false;
                            const isUpdated = !isNew && item.share_update === true;

                            return (
                              <div
                                key={`list-${item.id}-${idx}`}
                                className="d-flex align-items-start mb-2"
                                onClick={() => handleEditItem(item, "lists")}
                                style={{
                                  background: (isNew || isUpdated) ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.06)" : "rgba(var(--teal-bg-rag), 0.03)") : "var(--background)",
                                  border: isNew ? "1px solid var(--teal-bg)" : isUpdated ? "1px solid #f59e0b" : "1px solid var(--border-color)",
                                  borderRadius: "10px", padding: "10px 12px", cursor: "pointer",
                                }}
                              >
                                <div
                                  className={`task-checkbox ${!isPending ? 'checked' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleToggleStatus(item, "lists"); }}
                                  style={{ marginRight: "12px", flexShrink: 0 }}
                                >
                                  {!isPending && <i className="bi bi-check"></i>}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <p style={{
                                      margin: 0, fontSize: "0.85rem", fontWeight: 500, color: "var(--text)",
                                      textDecoration: !isPending ? "line-through" : "none",
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>{item.title}</p>
                                    {isNew && (
                                      <span style={{
                                        fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                        background: "var(--teal-bg)", color: "#fff",
                                        padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                      }}>New</span>
                                    )}
                                    {isUpdated && (
                                      <span style={{
                                        fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                        background: "#f59e0b", color: "#fff",
                                        padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                      }}>Updated</span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <div style={{
                                      fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: "3px",
                                      overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                                      WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                    }}>
                                      <span dangerouslySetInnerHTML={{ __html: item.description.substring(0, 200) }} />
                                    </div>
                                  )}
                                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                    {isPending
                                      ? <span className="message-success-label fw-normal" style={{ backgroundColor: "var(--orange-bg)", padding: "2px 10px" }}>Pending</span>
                                      : <span className="message-success-label fw-normal" style={{ padding: "2px 10px" }}>Completed</span>
                                    }
                                    {catLabel && (
                                      <span style={{
                                        fontSize: "0.6rem", fontWeight: 500,
                                        background: isDarkMode ? "rgba(var(--teal-bg-rag), 0.12)" : "rgba(var(--teal-bg-rag), 0.08)",
                                        color: "var(--teal-bg)", padding: "2px 8px", borderRadius: "6px",
                                      }}>
                                        <i className="bi bi-folder2 me-1"></i>{catLabel}
                                      </span>
                                    )}
                                    <span style={{
                                      fontSize: "0.6rem", fontWeight: 500,
                                      background: item._dir === "received"
                                        ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.1)" : "rgba(var(--teal-bg-rag), 0.06)")
                                        : (isDarkMode ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.06)"),
                                      color: item._dir === "received" ? "var(--teal-bg)" : "#f59e0b",
                                      padding: "2px 8px", borderRadius: "6px",
                                    }}>
                                      {item._dir === "received" ? <><i className="bi bi-arrow-down-short"></i>Received</> : <><i className="bi bi-arrow-up-short"></i>Sent</>}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className="btn py-0 px-1 flex-shrink-0 ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openConfirmPopup("Delete Shared List", "Remove this list?", async () => {
                                      await supabase.from("lists").delete().eq("id", item.id);
                                      setShareDetailListItems(prev => prev.filter(t => t.id !== item.id));
                                      fetchShareData();
                                    });
                                  }}
                                  title="Delete"
                                  style={{ background: "transparent", color: "var(--text)", opacity: 0.3, fontSize: "14px", marginTop: "2px" }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}

                {shareDetailTab === "notes" && (() => {
                  const noteItems = shareDetailNoteItems;
                  let filteredNotes = [...noteItems];
                  if (shareDetailDirection !== "all") filteredNotes = filteredNotes.filter(i => i._dir === shareDetailDirection);
                  const noteReceivedCount = noteItems.filter(i => i._dir === "received").length;
                  const noteSentCount = noteItems.filter(i => i._dir === "sent").length;

                  if (shareDetailCatFilter) {
                    if (shareDetailCatFilter === "general") {
                      filteredNotes = filteredNotes.filter(i => !i.category_id || i.category_id === 0);
                    } else {
                      const subIds = Object.entries(notesCatNameMap).filter(([, c]) => String(c.parentId) === String(shareDetailCatFilter)).map(([id]) => id);
                      const validIds = [String(shareDetailCatFilter), ...subIds];
                      filteredNotes = filteredNotes.filter(i => validIds.includes(String(i.category_id)));
                    }
                  }

                  const getNoteCatLabel = (catId) => {
                    if (!catId) return "General";
                    const cat = notesCatNameMap[catId];
                    if (!cat) return "General";
                    if (cat.parentName) return `${cat.parentName} \u203A ${cat.name}`;
                    if (cat.parentId && notesCatNameMap[cat.parentId]) return `${notesCatNameMap[cat.parentId].name} \u203A ${cat.name}`;
                    return cat.name;
                  };

                  const noteParentCatOptions = Object.entries(notesCatNameMap).filter(([, c]) => !c.parentId);

                  return (
                    <>
                      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-color)", flexShrink: 0 }}>
                        <div className="d-flex gap-1 mb-2">
                          {[
                            { key: "all", label: "All", count: noteItems.length, color: "var(--teal-bg)" },
                            { key: "received", label: "Received", count: noteReceivedCount, color: "var(--teal-bg)" },
                            { key: "sent", label: "Sent", count: noteSentCount, color: "#f59e0b" },
                          ].map(opt => (
                            <button
                              key={opt.key}
                              onClick={() => { setShareDetailDirection(opt.key); setShareDetailCatFilter(""); }}
                              style={{
                                fontSize: "0.72rem", fontWeight: shareDetailDirection === opt.key ? 600 : 400,
                                background: shareDetailDirection === opt.key ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.15)" : "rgba(var(--teal-bg-rag), 0.1)") : "transparent",
                                color: shareDetailDirection === opt.key ? opt.color : "var(--text)",
                                border: `1px solid ${shareDetailDirection === opt.key ? opt.color : "var(--border-color)"}`,
                                borderRadius: "8px", padding: "4px 12px", cursor: "pointer",
                                opacity: shareDetailDirection === opt.key ? 1 : 0.6,
                              }}
                            >
                              {opt.label} ({opt.count})
                            </button>
                          ))}
                        </div>
                        <div className="d-flex gap-2">
                          <select
                            value={shareDetailCatFilter}
                            onChange={(e) => setShareDetailCatFilter(e.target.value)}
                            style={{ fontSize: "0.78rem", borderRadius: "8px", padding: "5px 10px", border: "1px solid var(--border-color)", background: "var(--background)", color: "var(--text)", cursor: "pointer", outline: "none", flex: 1 }}
                          >
                            <option value="">All Categories</option>
                            {noteParentCatOptions.map(([id, cat]) => (
                              <option key={id} value={id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
                        {filteredNotes.length === 0 ? (
                          <div className="text-center py-4" style={{ color: "var(--text)", opacity: 0.4, fontSize: "0.85rem" }}>
                            No notes found.
                          </div>
                        ) : (
                          filteredNotes.map((item, idx) => {
                            const isPending = item.status !== "completed";
                            const catLabel = getNoteCatLabel(item.category_id);
                            const isNew = item._dir === "received" && item.share_visible === false;
                            const isUpdated = !isNew && item.share_update === true;

                            return (
                              <div
                                key={`note-${item.id}-${idx}`}
                                className="d-flex align-items-start mb-2"
                                onClick={() => handleEditItem(item, "notes")}
                                style={{
                                  background: (isNew || isUpdated) ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.06)" : "rgba(var(--teal-bg-rag), 0.03)") : "var(--background)",
                                  border: isNew ? "1px solid var(--teal-bg)" : isUpdated ? "1px solid #f59e0b" : "1px solid var(--border-color)",
                                  borderRadius: "10px", padding: "10px 12px", cursor: "pointer",
                                }}
                              >
                                <div
                                  className={`task-checkbox ${!isPending ? 'checked' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleToggleStatus(item, "notes"); }}
                                  style={{ marginRight: "12px", flexShrink: 0 }}
                                >
                                  {!isPending && <i className="bi bi-check"></i>}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <p style={{
                                      margin: 0, fontSize: "0.85rem", fontWeight: 500, color: "var(--text)",
                                      textDecoration: !isPending ? "line-through" : "none",
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>{item.title}</p>
                                    {isNew && (
                                      <span style={{
                                        fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                        background: "var(--teal-bg)", color: "#fff",
                                        padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                      }}>New</span>
                                    )}
                                    {isUpdated && (
                                      <span style={{
                                        fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                        background: "#f59e0b", color: "#fff",
                                        padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                      }}>Updated</span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <div style={{
                                      fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: "3px",
                                      overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                                      WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                    }}>
                                      <span dangerouslySetInnerHTML={{ __html: item.description.substring(0, 200) }} />
                                    </div>
                                  )}
                                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                    {isPending
                                      ? <span className="message-success-label fw-normal" style={{ backgroundColor: "var(--orange-bg)", padding: "2px 10px" }}>Pending</span>
                                      : <span className="message-success-label fw-normal" style={{ padding: "2px 10px" }}>Completed</span>
                                    }
                                    {catLabel && (
                                      <span style={{
                                        fontSize: "0.6rem", fontWeight: 500,
                                        background: isDarkMode ? "rgba(var(--teal-bg-rag), 0.12)" : "rgba(var(--teal-bg-rag), 0.08)",
                                        color: "var(--teal-bg)", padding: "2px 8px", borderRadius: "6px",
                                      }}>
                                        <i className="bi bi-folder2 me-1"></i>{catLabel}
                                      </span>
                                    )}
                                    <span style={{
                                      fontSize: "0.6rem", fontWeight: 500,
                                      background: item._dir === "received"
                                        ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.1)" : "rgba(var(--teal-bg-rag), 0.06)")
                                        : (isDarkMode ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.06)"),
                                      color: item._dir === "received" ? "var(--teal-bg)" : "#f59e0b",
                                      padding: "2px 8px", borderRadius: "6px",
                                    }}>
                                      {item._dir === "received" ? <><i className="bi bi-arrow-down-short"></i>Received</> : <><i className="bi bi-arrow-up-short"></i>Sent</>}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className="btn py-0 px-1 flex-shrink-0 ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openConfirmPopup("Delete Shared Note", "Remove this note?", async () => {
                                      await supabase.from("notes").delete().eq("id", item.id);
                                      setShareDetailNoteItems(prev => prev.filter(t => t.id !== item.id));
                                      fetchShareData();
                                    });
                                  }}
                                  title="Delete"
                                  style={{ background: "transparent", color: "var(--text)", opacity: 0.3, fontSize: "14px", marginTop: "2px" }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}

                {shareDetailTab === "grocery" && (() => {
                  const groceryItems = shareDetailGroceryItems;
                  let filteredGrocery = [...groceryItems];
                  if (shareDetailDirection !== "all") filteredGrocery = filteredGrocery.filter(i => i._dir === shareDetailDirection);
                  const groceryReceivedCount = groceryItems.filter(i => i._dir === "received").length;
                  const grocerySentCount = groceryItems.filter(i => i._dir === "sent").length;

                  if (shareDetailCatFilter) {
                    if (shareDetailCatFilter === "general") {
                      filteredGrocery = filteredGrocery.filter(i => !i.category_id || i.category_id === 0);
                    } else {
                      const subIds = Object.entries(groceryCatNameMap).filter(([, c]) => String(c.parentId) === String(shareDetailCatFilter)).map(([id]) => id);
                      const validIds = [String(shareDetailCatFilter), ...subIds];
                      filteredGrocery = filteredGrocery.filter(i => validIds.includes(String(i.category_id)));
                    }
                  }

                  const getGroceryCatLabel = (catId) => {
                    if (!catId) return "General";
                    const cat = groceryCatNameMap[catId];
                    if (!cat) return "General";
                    if (cat.parentName) return `${cat.parentName} \u203A ${cat.name}`;
                    if (cat.parentId && groceryCatNameMap[cat.parentId]) return `${groceryCatNameMap[cat.parentId].name} \u203A ${cat.name}`;
                    return cat.name;
                  };

                  const groceryParentCatOptions = Object.entries(groceryCatNameMap).filter(([, c]) => !c.parentId);

                  return (
                    <>
                      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-color)", flexShrink: 0 }}>
                        <div className="d-flex gap-1 mb-2">
                          {[
                            { key: "all", label: "All", count: groceryItems.length, color: "var(--teal-bg)" },
                            { key: "received", label: "Received", count: groceryReceivedCount, color: "var(--teal-bg)" },
                            { key: "sent", label: "Sent", count: grocerySentCount, color: "#f59e0b" },
                          ].map(opt => (
                            <button
                              key={opt.key}
                              onClick={() => { setShareDetailDirection(opt.key); setShareDetailCatFilter(""); }}
                              style={{
                                fontSize: "0.72rem", fontWeight: shareDetailDirection === opt.key ? 600 : 400,
                                background: shareDetailDirection === opt.key ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.15)" : "rgba(var(--teal-bg-rag), 0.1)") : "transparent",
                                color: shareDetailDirection === opt.key ? opt.color : "var(--text)",
                                border: `1px solid ${shareDetailDirection === opt.key ? opt.color : "var(--border-color)"}`,
                                borderRadius: "8px", padding: "4px 12px", cursor: "pointer",
                                opacity: shareDetailDirection === opt.key ? 1 : 0.6,
                              }}
                            >
                              {opt.label} ({opt.count})
                            </button>
                          ))}
                        </div>
                        <div className="d-flex gap-2">
                          <select
                            value={shareDetailCatFilter}
                            onChange={(e) => setShareDetailCatFilter(e.target.value)}
                            style={{ fontSize: "0.78rem", borderRadius: "8px", padding: "5px 10px", border: "1px solid var(--border-color)", background: "var(--background)", color: "var(--text)", cursor: "pointer", outline: "none", flex: 1 }}
                          >
                            <option value="">All Categories</option>
                            {groceryParentCatOptions.map(([id, cat]) => (
                              <option key={id} value={id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
                        {filteredGrocery.length === 0 ? (
                          <div className="text-center py-4" style={{ color: "var(--text)", opacity: 0.4, fontSize: "0.85rem" }}>
                            No grocery items found.
                          </div>
                        ) : (
                          filteredGrocery.map((item, idx) => {
                            const isPending = item.status !== "completed";
                            const catLabel = getGroceryCatLabel(item.category_id);
                            const isNew = item._dir === "received" && item.share_visible === false;
                            const isUpdated = !isNew && item.share_update === true;

                            return (
                              <div
                                key={`grocery-${item.id}-${idx}`}
                                className="d-flex align-items-start mb-2"
                                onClick={() => handleEditItem(item, "grocery")}
                                style={{
                                  background: (isNew || isUpdated) ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.06)" : "rgba(var(--teal-bg-rag), 0.03)") : "var(--background)",
                                  border: isNew ? "1px solid var(--teal-bg)" : isUpdated ? "1px solid #f59e0b" : "1px solid var(--border-color)",
                                  borderRadius: "10px", padding: "10px 12px", cursor: "pointer",
                                }}
                              >
                                <div
                                  className={`task-checkbox ${!isPending ? 'checked' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleToggleStatus(item, "grocery"); }}
                                  style={{ marginRight: "12px", flexShrink: 0 }}
                                >
                                  {!isPending && <i className="bi bi-check"></i>}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <p style={{
                                      margin: 0, fontSize: "0.85rem", fontWeight: 500, color: "var(--text)",
                                      textDecoration: !isPending ? "line-through" : "none",
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>{item.title}</p>
                                    {isNew && (
                                      <span style={{
                                        fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                        background: "var(--teal-bg)", color: "#fff",
                                        padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                      }}>New</span>
                                    )}
                                    {isUpdated && (
                                      <span style={{
                                        fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase",
                                        background: "#f59e0b", color: "#fff",
                                        padding: "1px 6px", borderRadius: "4px", flexShrink: 0, lineHeight: "14px",
                                      }}>Updated</span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <div style={{
                                      fontSize: "0.75rem", color: isDarkMode ? "#94a3b8" : "#64748b", marginTop: "3px",
                                      overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                                      WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                    }}>
                                      <span dangerouslySetInnerHTML={{ __html: item.description.substring(0, 200) }} />
                                    </div>
                                  )}
                                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                    {isPending
                                      ? <span className="message-success-label fw-normal" style={{ backgroundColor: "var(--orange-bg)", padding: "2px 10px" }}>Pending</span>
                                      : <span className="message-success-label fw-normal" style={{ padding: "2px 10px" }}>Completed</span>
                                    }
                                    {catLabel && (
                                      <span style={{
                                        fontSize: "0.6rem", fontWeight: 500,
                                        background: isDarkMode ? "rgba(var(--teal-bg-rag), 0.12)" : "rgba(var(--teal-bg-rag), 0.08)",
                                        color: "var(--teal-bg)", padding: "2px 8px", borderRadius: "6px",
                                      }}>
                                        <i className="bi bi-folder2 me-1"></i>{catLabel}
                                      </span>
                                    )}
                                    <span style={{
                                      fontSize: "0.6rem", fontWeight: 500,
                                      background: item._dir === "received"
                                        ? (isDarkMode ? "rgba(var(--teal-bg-rag), 0.1)" : "rgba(var(--teal-bg-rag), 0.06)")
                                        : (isDarkMode ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.06)"),
                                      color: item._dir === "received" ? "var(--teal-bg)" : "#f59e0b",
                                      padding: "2px 8px", borderRadius: "6px",
                                    }}>
                                      {item._dir === "received" ? <><i className="bi bi-arrow-down-short"></i>Received</> : <><i className="bi bi-arrow-up-short"></i>Sent</>}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className="btn py-0 px-1 flex-shrink-0 ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openConfirmPopup("Delete Shared Grocery", "Remove this grocery item?", async () => {
                                      await supabase.from("grocery_table").delete().eq("id", item.id);
                                      setShareDetailGroceryItems(prev => prev.filter(t => t.id !== item.id));
                                      fetchShareData();
                                    });
                                  }}
                                  title="Delete"
                                  style={{ background: "transparent", color: "var(--text)", opacity: 0.3, fontSize: "14px", marginTop: "2px" }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}

              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowShareDetailPopup(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Popup for shared items */}
      {editItem && (
        <EditPopup
          show={showEditPopup}
          handleClose={() => { setShowEditPopup(false); setEditItem(null); }}
          handleSave={handleSaveEdit}
          item={editItem}
          categoriesData={editCatData.categories}
          fetchSubcategories={() => Promise.resolve(editCatData.subcategories)}
          subcategoriesData={editCatData.subcategories}
          sharedCategories={[]}
          showDueDate={editSectionType === "tasks"}
          titleAction={{ tasks: "Tasks", lists: "Lists", notes: "Notes", grocery: "Grocery" }[editSectionType] || "Tasks"}
          handleDelete={(itemId) => {
            const table = TABLE_MAP[editSectionType];
            const setter = getStateSetter(editSectionType);
            openConfirmPopup("Delete Shared Item", "Remove this item?", async () => {
              await supabase.from(table).delete().eq("id", itemId);
              setter(prev => prev.filter(t => t.id !== itemId));
              setShowEditPopup(false);
              setEditItem(null);
              fetchShareData();
            });
          }}
        />
      )}
    </div>
  );
}

export default SharedWithMe;
